import { Skeleton } from '@slal/ui/animations';
import { Checkbox } from '@slal/ui/forms';
import { PDFIcon } from '@slal/ui/icons';
import { Anchor } from '@slal/ui/navigation';
import { Article, P } from '@slal/ui/typography';
import { isNumber, uniqueId } from 'lodash';
import { ChangeEventHandler, useCallback } from 'react';
import { Card, DescriptionList, TBody, TD, TH, THead, TR, Table } from '~/components';
import { StyledFundList, StyledFundListItem, StyledTableHeadingRoundel } from './styles';
import { FundHelpModalProps, FundItemProps, FundListProps, FundTableProps } from './types';

const label = 'Fund options';
const undefinedText = '-';
const headers = ['Fund name', 'Your total fund charge', 'Volatility rating', 'Select fund', 'Fund factsheet'];

export const FundSelectCheckbox = ({ fundAllocations, handleSelect, ...fund }: FundItemProps) => {
    const { fundCode } = fund;

    const handleChange: ChangeEventHandler = useCallback(() => {
        handleSelect(fund);
    }, [handleSelect, fund]);

    return (
        <Checkbox
            checked={!!fundAllocations?.find(fundAllocation => fundAllocation.fundCode === fundCode)}
            onChange={handleChange}
            aria-label="Select fund"
        />
    );
};

export const FundListItem = ({ fundAllocations, handleSelect, ...fund }: FundItemProps) => {
    const { fundName, factsheetUrl, totalExpenseRate, volatilityRating } = fund;
    return (
        <StyledFundListItem aria-label={fundName}>
            <Card>
                <DescriptionList
                    items={[
                        {
                            label: `${headers[0]}:`,
                            value: fundName,
                        },
                        {
                            label: `${headers[1]}:`,
                            value: isNumber(totalExpenseRate) ? `${totalExpenseRate}%` : undefinedText,
                        },
                        {
                            label: `${headers[2]}:`,
                            value: volatilityRating || undefinedText,
                        },
                        {
                            label: `${headers[4]}:`,
                            value: factsheetUrl ? (
                                <Anchor href={factsheetUrl} target="_blank">
                                    <PDFIcon title={`Fund factsheet for ${fundName}`} />
                                </Anchor>
                            ) : (
                                undefinedText
                            ),
                        },
                        {
                            label: `${headers[3]}:`,
                            value: (
                                <FundSelectCheckbox
                                    fundAllocations={fundAllocations}
                                    {...fund}
                                    handleSelect={handleSelect}
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        </StyledFundListItem>
    );
};

export const FundList = ({ fundAllocations, funds, handleSelect, ...otherProps }: FundListProps) => (
    <StyledFundList {...otherProps} aria-label={label}>
        {!!funds?.length &&
            funds.map(fund => (
                <FundListItem
                    key={fund.fundCode}
                    {...fund}
                    fundAllocations={fundAllocations}
                    handleSelect={handleSelect}
                />
            ))}
    </StyledFundList>
);

export const FundListLoading = () => (
    <StyledFundList aria-label={label}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(id => (
            <StyledFundListItem key={id}>
                <Card>
                    <Article>
                        <P>
                            <Skeleton />
                        </P>
                        <P>
                            <Skeleton width="80%" />
                        </P>
                        <P>
                            <Skeleton width="40%" />
                        </P>
                        <P>
                            <Skeleton width="60%" />
                        </P>
                    </Article>
                </Card>
            </StyledFundListItem>
        ))}
    </StyledFundList>
);

export const FundTableHeader = ({
    handleOpenVolatilityExplainedModal,
    handleOpenFundChargesModal,
}: FundHelpModalProps) => (
    <THead>
        <TR>
            {headers.map((header, index) => (
                <TH scope="col" key={index} aria-label={header}>
                    {header}
                    {header === 'Volatility rating' && (
                        <StyledTableHeadingRoundel
                            role="button"
                            onClick={handleOpenVolatilityExplainedModal}
                            aria-label="More information about volatility ratings"
                        >
                            ?
                        </StyledTableHeadingRoundel>
                    )}
                    {header === 'Your total fund charge' && (
                        <StyledTableHeadingRoundel
                            role="button"
                            onClick={handleOpenFundChargesModal}
                            aria-label="More information about your total fund charge"
                        >
                            ?
                        </StyledTableHeadingRoundel>
                    )}
                </TH>
            ))}
        </TR>
    </THead>
);

export const FundTR = ({ fundAllocations, handleSelect, ...fund }: FundItemProps) => {
    const id = uniqueId('fund-');
    const { fundName, factsheetUrl, totalExpenseRate, volatilityRating } = fund;
    return (
        <TR aria-labelledby={id}>
            <TD id={id}>{fundName}</TD>
            <TD>{isNumber(totalExpenseRate) ? `${totalExpenseRate}%` : undefinedText}</TD>
            <TD>{volatilityRating || undefinedText}</TD>
            <TD>
                <FundSelectCheckbox fundAllocations={fundAllocations} {...fund} handleSelect={handleSelect} />
            </TD>
            <TD>
                {factsheetUrl ? (
                    <Anchor href={factsheetUrl} target="_blank">
                        <PDFIcon title={`Fund factsheet for ${fundName}`} />
                    </Anchor>
                ) : (
                    undefinedText
                )}
            </TD>
        </TR>
    );
};

export const FundTable = ({
    funds,
    handleSelect,
    handleOpenVolatilityExplainedModal,
    handleOpenFundChargesModal,
    fundAllocations,
    ...otherProps
}: FundTableProps & FundHelpModalProps) => {
    return (
        <Table {...otherProps} aria-label={label}>
            <FundTableHeader
                handleOpenFundChargesModal={handleOpenFundChargesModal}
                handleOpenVolatilityExplainedModal={handleOpenVolatilityExplainedModal}
            />
            <TBody>
                {!!funds?.length &&
                    funds.map(fund => (
                        <FundTR
                            key={fund.fundCode}
                            {...fund}
                            handleSelect={handleSelect}
                            fundAllocations={fundAllocations}
                        />
                    ))}
            </TBody>
        </Table>
    );
};

export const FundTableLoading = ({
    handleOpenVolatilityExplainedModal,
    handleOpenFundChargesModal,
}: FundHelpModalProps) => (
    <Table aria-label={label}>
        <FundTableHeader
            handleOpenFundChargesModal={handleOpenFundChargesModal}
            handleOpenVolatilityExplainedModal={handleOpenVolatilityExplainedModal}
        />
        <TBody>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(id => (
                <TR key={id}>
                    <TD>
                        <Skeleton />
                    </TD>
                    <TD>
                        <Skeleton width="50%" />
                    </TD>
                    <TD>
                        <Skeleton width="80%" />
                    </TD>
                    <TD>
                        <Skeleton width="15%" />
                    </TD>
                    <TD>
                        <Skeleton width="20%" />
                    </TD>
                </TR>
            ))}
        </TBody>
    </Table>
);
